import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import type { PlateDecodeFields, Vehicle, VinDecodeFields } from "@autorepair/contracts";
import { ApiError, customersApi, vehicleReferenceApi, vehiclesApi, tenantSettingsApi, workOrdersApi } from "../lib/api";
import { composeEngineDescription, VIN_FIELD_LABELS } from "../lib/vinDecode";
import { PLATE_DECODER_ENABLED, PLATE_FIELD_LABELS, US_STATE_CODES } from "../lib/plateDecode";
import { useConfirmGate } from "../lib/useConfirm";
import { Spinner } from "../components/ui-modern";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernConfirmButton,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernSelect,
} from "../components/ui-modern";

/** Sentinel option value for "none of these — let me type it in" — matches
 * VehicleForm.tsx's own OTHER sentinel exactly. */
const OTHER = "__other__";

function initialValues(vehicle: Vehicle) {
  return {
    vin: vehicle.vin ?? "",
    modelYear: vehicle.modelYear != null ? String(vehicle.modelYear) : "",
    make: vehicle.make ?? "",
    model: vehicle.model ?? "",
    trim: vehicle.trim ?? "",
    engineDescription: vehicle.engineDescription ?? "",
    licensePlate: vehicle.licensePlate ?? "",
    licensePlateState: vehicle.licensePlateState ?? "",
    imageUrl: vehicle.imageUrl ?? "",
    lastOdometer: vehicle.lastOdometer != null ? String(vehicle.lastOdometer) : "",
  };
}

/**
 * The modern theme's Edit Vehicle page — see docs/FRONTEND_BACKLOG.md
 * Round 62 and NewVehiclePageModern.tsx's own header comment for why this
 * inlines VehicleForm.tsx's field set/logic (VIN decode, the catalog
 * picker, the plate decoder gated by `PLATE_DECODER_ENABLED`) rather than
 * reusing that out-of-scope shared component. Same
 * queries/mutations/invalidation as EditVehiclePageClassic.tsx: the
 * customer/vehicle/work-orders queries, the `canDelete` gate (mirrors
 * `VehiclesService.delete()`'s own "no ACTIVE work order against it"
 * check), and the delete mutation's `["vehicles", "byCustomer", ...]`
 * invalidation, all duplicated verbatim rather than shared.
 */
export function EditVehiclePageModern() {
  const { customerNumber: customerNumberParam, vehicleId } = useParams();
  const customerNumber = Number(customerNumberParam);
  const vehicleIdNum = Number(vehicleId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const customerQuery = useQuery({
    queryKey: ["customer", "by-number", customerNumber],
    queryFn: () => customersApi.getByNumber(customerNumber),
  });
  const customerIdNum = customerQuery.data?.customer.id as number;
  const vehicleQuery = useQuery({ queryKey: ["vehicle", vehicleIdNum], queryFn: () => vehiclesApi.get(vehicleIdNum) });
  const vehicleWorkOrdersQuery = useQuery({
    queryKey: ["work-orders", "byVehicle", "active", vehicleIdNum],
    queryFn: () => workOrdersApi.list({ vehicleId: vehicleIdNum, limit: 1 }),
  });
  const canDelete = vehicleWorkOrdersQuery.data?.total === 0;

  const deleteMutation = useMutation({
    mutationFn: () => vehiclesApi.delete(vehicleIdNum),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "byCustomer", customerIdNum] });
      navigate(`/customers/${customerNumber}`);
    },
    onError: (err: unknown) => setNotice(err instanceof Error ? err.message : "Something went wrong. Please try again."),
  });

  const vehicle = vehicleQuery.data?.vehicle;

  const [values, setValues] = useState<ReturnType<typeof initialValues> | null>(null);
  const current = values ?? (vehicle ? initialValues(vehicle) : null);

  function set<K extends keyof ReturnType<typeof initialValues>>(key: K, value: ReturnType<typeof initialValues>[K]): void {
    setValues((v) => ({ ...(v ?? initialValues(vehicle!)), [key]: value }));
  }

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [decodeNotice, setDecodeNotice] = useState<string | null>(null);
  const [decodedFields, setDecodedFields] = useState<VinDecodeFields | null>(null);
  const [plateState, setPlateState] = useState(vehicle?.licensePlateState || "TX");
  const [decodingPlate, setDecodingPlate] = useState(false);
  const [plateDecodeNotice, setPlateDecodeNotice] = useState<string | null>(null);
  const [plateDecodedFields, setPlateDecodedFields] = useState<PlateDecodeFields | null>(null);
  const confirmGate = useConfirmGate();

  // Editing an existing vehicle defaults to manual/free-text mode (its
  // values may not match any catalog entry) — matches VehicleForm.tsx's own
  // `useState(!vehicle)`.
  const [catalogMode, setCatalogMode] = useState(false);
  const [makeId, setMakeId] = useState<number | null>(null);
  const [modelId, setModelId] = useState<number | null>(null);
  const [trimIsManual, setTrimIsManual] = useState(false);

  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });
  const vehicleImageEnabled = tenantSettingsQuery.data?.settings.vehicleImageEnabled ?? false;

  const makesQuery = useQuery({ queryKey: ["vehicle-reference", "makes"], queryFn: vehicleReferenceApi.listMakes, enabled: catalogMode });
  const modelsQuery = useQuery({
    queryKey: ["vehicle-reference", "models", makeId],
    queryFn: () => vehicleReferenceApi.listModels(makeId!),
    enabled: catalogMode && makeId != null,
  });
  const yearsQuery = useQuery({
    queryKey: ["vehicle-reference", "years", modelId],
    queryFn: () => vehicleReferenceApi.listYears(modelId!),
    enabled: catalogMode && modelId != null,
  });
  const yearNumber = Number(current?.modelYear);
  const trimsQuery = useQuery({
    queryKey: ["vehicle-reference", "trims", modelId, current?.modelYear],
    queryFn: () => vehicleReferenceApi.listTrims(modelId!, yearNumber),
    enabled: catalogMode && modelId != null && !!current?.modelYear && Number.isInteger(yearNumber),
  });
  const trimHasOptions = (trimsQuery.data?.trims.length ?? 0) > 0;

  async function switchToCatalogMode(): Promise<void> {
    if (!current) return;
    setCatalogMode(true);
    setTrimIsManual(false);

    const typedMake = current.make.trim();
    const typedModel = current.model.trim();
    const typedYear = current.modelYear.trim();
    const typedTrim = current.trim.trim();

    function giveUp(from: "make" | "model" | "year" | "trim"): void {
      if (from === "make") {
        setMakeId(null);
        setModelId(null);
        set("make", "");
        set("model", "");
        set("modelYear", "");
        set("trim", "");
      } else if (from === "model") {
        setModelId(null);
        set("model", "");
        set("modelYear", "");
        set("trim", "");
      } else if (from === "year") {
        set("modelYear", "");
        set("trim", "");
      } else {
        set("trim", "");
      }
    }

    if (!typedMake) return giveUp("make");
    try {
      const makes = await queryClient.fetchQuery({ queryKey: ["vehicle-reference", "makes"], queryFn: vehicleReferenceApi.listMakes });
      const matchedMake = makes.makes.find((m) => m.name.toLowerCase() === typedMake.toLowerCase());
      if (!matchedMake) return giveUp("make");
      setMakeId(matchedMake.id);
      set("make", matchedMake.name);

      if (!typedModel) return giveUp("model");
      const models = await queryClient.fetchQuery({
        queryKey: ["vehicle-reference", "models", matchedMake.id],
        queryFn: () => vehicleReferenceApi.listModels(matchedMake.id),
      });
      const matchedModel = models.models.find((m) => m.name.toLowerCase() === typedModel.toLowerCase());
      if (!matchedModel) return giveUp("model");
      setModelId(matchedModel.id);
      set("model", matchedModel.name);

      const yearNum = Number(typedYear);
      if (!typedYear || !Number.isInteger(yearNum)) return giveUp("year");
      const years = await queryClient.fetchQuery({
        queryKey: ["vehicle-reference", "years", matchedModel.id],
        queryFn: () => vehicleReferenceApi.listYears(matchedModel.id),
      });
      if (!years.years.some((y) => y.modelYear === yearNum)) return giveUp("year");

      if (!typedTrim) return giveUp("trim");
      const trims = await queryClient.fetchQuery({
        queryKey: ["vehicle-reference", "trims", matchedModel.id, typedYear],
        queryFn: () => vehicleReferenceApi.listTrims(matchedModel.id, yearNum),
      });
      const matchedTrim = trims.trims.find((t) => t.name.toLowerCase() === typedTrim.toLowerCase());
      if (matchedTrim) {
        set("trim", matchedTrim.name);
      } else if (trims.trims.length > 0) {
        setTrimIsManual(true);
      } else {
        set("trim", "");
      }
    } catch {
      giveUp("make");
    }
  }

  function selectMake(raw: string): void {
    if (raw === OTHER) {
      setCatalogMode(false);
      return;
    }
    const idNum = Number(raw);
    const name = makesQuery.data?.makes.find((m) => m.id === idNum)?.name ?? "";
    setMakeId(idNum);
    setModelId(null);
    setTrimIsManual(false);
    set("make", name);
    set("model", "");
    set("trim", "");
    set("modelYear", "");
  }

  function selectModel(raw: string): void {
    const idNum = Number(raw);
    const name = modelsQuery.data?.models.find((m) => m.id === idNum)?.name ?? "";
    setModelId(idNum);
    setTrimIsManual(false);
    set("model", name);
    set("trim", "");
    set("modelYear", "");
  }

  function selectYear(raw: string): void {
    setTrimIsManual(false);
    set("modelYear", raw);
    set("trim", "");
  }

  function selectTrim(raw: string): void {
    if (raw === OTHER) {
      setTrimIsManual(true);
      set("trim", "");
      return;
    }
    set("trim", raw);
  }

  async function decodeVin(fillBlanks: boolean): Promise<void> {
    const vinValue = (current?.vin ?? "").trim();
    if (!vinValue) return;
    setDecodeNotice(null);
    setDecoding(true);
    try {
      const result = await vehiclesApi.decodeVin(vinValue);
      if (result.status === "unavailable") {
        setDecodedFields(null);
        if (fillBlanks) setDecodeNotice("VIN lookup is unavailable right now — you can still enter the vehicle's details by hand.");
        return;
      }
      const { fields } = result;
      setDecodedFields(fields);
      if (fillBlanks) {
        setCatalogMode(false);
        setMakeId(null);
        setModelId(null);
        set("modelYear", current?.modelYear || (fields.modelYear ? String(fields.modelYear) : current?.modelYear ?? ""));
        set("make", current?.make || fields.make || current?.make || "");
        set("model", current?.model || fields.model || current?.model || "");
        set("trim", current?.trim || fields.trim || current?.trim || "");
        set(
          "engineDescription",
          current?.engineDescription || composeEngineDescription(fields) || current?.engineDescription || "",
        );
        setDecodeNotice(result.cached ? "Loaded from a previous lookup of this VIN." : "Decoded successfully.");
      }
    } catch {
      setDecodedFields(null);
      if (fillBlanks) setDecodeNotice("VIN lookup failed — you can still enter the vehicle's details by hand.");
    } finally {
      setDecoding(false);
    }
  }

  async function decodePlate(fillBlanks: boolean): Promise<void> {
    const plate = (current?.licensePlate ?? "").trim();
    const state = plateState.trim();
    if (!plate || !state) return;
    setPlateDecodeNotice(null);
    setDecodingPlate(true);
    try {
      const result = await vehiclesApi.decodePlate(plate, state);
      if (result.status === "not_configured") {
        setPlateDecodedFields(null);
        if (fillBlanks) setPlateDecodeNotice("License plate lookup isn't set up yet — you can still enter the vehicle's details by hand.");
        return;
      }
      if (result.status === "unavailable") {
        setPlateDecodedFields(null);
        if (fillBlanks) setPlateDecodeNotice("License plate lookup is unavailable right now — you can still enter the vehicle's details by hand.");
        return;
      }
      const { fields } = result;
      setPlateDecodedFields(fields);
      if (fillBlanks) {
        setCatalogMode(false);
        setMakeId(null);
        setModelId(null);
        set("licensePlateState", state);
        set("vin", current?.vin || fields.vin || current?.vin || "");
        set("modelYear", current?.modelYear || (fields.modelYear ? String(fields.modelYear) : current?.modelYear ?? ""));
        set("make", current?.make || fields.make || current?.make || "");
        set("model", current?.model || fields.model || current?.model || "");
        set("trim", current?.trim || fields.trim || current?.trim || "");
        set("engineDescription", current?.engineDescription || fields.engineDescription || current?.engineDescription || "");
        setPlateDecodeNotice(result.cached ? "Loaded from a previous lookup of this plate." : "Decoded successfully.");
      }
    } catch {
      setPlateDecodedFields(null);
      if (fillBlanks) setPlateDecodeNotice("License plate lookup failed — you can still enter the vehicle's details by hand.");
    } finally {
      setDecodingPlate(false);
    }
  }

  // Silently loads the read-only "From the VIN"/"From the Plate" panels for
  // a vehicle that already has one on file — same cache-hit guarantee (and
  // same PLATE_DECODER_ENABLED gate) as VehicleForm.tsx's own two effects.
  useEffect(() => {
    if (vehicle?.vin) void decodeVin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.vin]);
  useEffect(() => {
    if (PLATE_DECODER_ENABLED && vehicle?.licensePlate && vehicle?.licensePlateState) void decodePlate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.licensePlate, vehicle?.licensePlateState]);

  async function save(): Promise<void> {
    if (!current) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await vehiclesApi.update(vehicleIdNum, {
        vin: current.vin.trim() || null,
        modelYear: current.modelYear ? Number(current.modelYear) : null,
        make: current.make.trim() || null,
        model: current.model.trim() || null,
        trim: current.trim.trim() || null,
        engineDescription: current.engineDescription.trim() || null,
        licensePlate: current.licensePlate.trim() || null,
        licensePlateState: current.licensePlateState.trim() || null,
        imageUrl: current.imageUrl.trim() || null,
        lastOdometer: current.lastOdometer ? Number(current.lastOdometer) : null,
      });
      queryClient.setQueryData(["vehicle", vehicleIdNum], result);
      navigate(`/customers/${customerNumber}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save this vehicle. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (customerQuery.isLoading || vehicleQuery.isLoading) return <Spinner label="Loading vehicle…" />;
  if (customerQuery.error || !customerQuery.data || vehicleQuery.error || !vehicleQuery.data || !current) {
    return <ModernErrorBanner message="Could not load this vehicle." />;
  }

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs
        items={[
          { label: "Customers", to: "/customers" },
          { label: customerQuery.data.customer.displayName, to: `/customers/${customerNumber}` },
          { label: "Edit Vehicle" },
        ]}
      />
      <h1 className="m-0 mb-4 text-[27px] font-bold text-[#071a33]">Edit Vehicle</h1>
      {notice && <ModernErrorBanner message={notice} />}

      <ModernCard>
        <div className="mb-5 rounded-[10px] bg-[#e6f1f5] px-3.5 py-3">
          <div className="text-[12.5px] text-[#4a5361]">Customer</div>
          <div className="text-[17px] font-semibold text-[#071a33]">{customerQuery.data.customer.displayName}</div>
        </div>

        <ModernField label="VIN" htmlFor="vin">
          <div className="flex gap-2">
            <ModernInput id="vin" value={current.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} />
            <ModernButton
              type="button"
              variant="secondary"
              className="whitespace-nowrap"
              onClick={() => void decodeVin(true)}
              disabled={decoding || !current.vin.trim()}
            >
              {decoding ? "Looking up…" : "Decode VIN"}
            </ModernButton>
          </div>
          <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Optional — decode it to fill in the details below automatically.</p>
          {decodeNotice && <p className="mt-1 text-[13px] text-[#4a5361]">{decodeNotice}</p>}
        </ModernField>

        {decodedFields &&
          (decodedFields.modelYear || decodedFields.make || decodedFields.model || VIN_FIELD_LABELS.some(([key]) => decodedFields[key])) && (
            <div className="mb-5 rounded-[10px] border border-dashed border-[#e2e7ee] px-3.5 py-3">
              <h3 className="mb-2 text-[14px] font-bold text-[#071a33]">From the VIN</h3>
              <dl className="grid grid-cols-2 gap-y-1 text-[13px]">
                {decodedFields.modelYear && (
                  <div className="contents">
                    <dt className="text-[#6b7a8f]">Year</dt>
                    <dd className="text-right text-[#101b2d]">{decodedFields.modelYear}</dd>
                  </div>
                )}
                {decodedFields.make && (
                  <div className="contents">
                    <dt className="text-[#6b7a8f]">Make</dt>
                    <dd className="text-right text-[#101b2d]">{decodedFields.make}</dd>
                  </div>
                )}
                {decodedFields.model && (
                  <div className="contents">
                    <dt className="text-[#6b7a8f]">Model</dt>
                    <dd className="text-right text-[#101b2d]">{decodedFields.model}</dd>
                  </div>
                )}
                {VIN_FIELD_LABELS.filter(([key]) => decodedFields[key]).map(([key, label]) => (
                  <div key={key} className="contents">
                    <dt className="text-[#6b7a8f]">{label}</dt>
                    <dd className="text-right text-[#101b2d]">{String(decodedFields[key])}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmGate.request(() => void save());
          }}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[14px] font-semibold text-[#071a33]">Make / Model / Year / Trim</span>
            <button
              type="button"
              className="text-[13px] text-[#0f6d86] underline"
              onClick={() => (catalogMode ? setCatalogMode(false) : void switchToCatalogMode())}
            >
              {catalogMode ? "Type it in manually instead" : "Use the Make/Model/Year/Trim picker instead"}
            </button>
          </div>

          {catalogMode ? (
            <>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Make" htmlFor="make">
                  <ModernSelect id="make" value={makeId ?? ""} onChange={(e) => selectMake(e.target.value)}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {makesQuery.data?.makes.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                    <option value={OTHER}>Other — type it in</option>
                  </ModernSelect>
                </ModernField>
                <ModernField label="Model" htmlFor="model">
                  <ModernSelect id="model" value={modelId ?? ""} onChange={(e) => selectModel(e.target.value)} disabled={makeId == null || modelsQuery.isLoading}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {modelsQuery.data?.models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </ModernSelect>
                  {makeId == null && <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Choose a make first</p>}
                </ModernField>
              </div>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Year" htmlFor="year">
                  <ModernSelect id="year" value={current.modelYear} onChange={(e) => selectYear(e.target.value)} disabled={modelId == null || yearsQuery.isLoading}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {yearsQuery.data?.years.map((y) => (
                      <option key={y.modelYear} value={y.modelYear}>
                        {y.modelYear}
                      </option>
                    ))}
                  </ModernSelect>
                  {modelId == null && <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Choose a model first</p>}
                </ModernField>
                <ModernField label="Trim" htmlFor="trim">
                  {trimHasOptions && !trimIsManual ? (
                    <ModernSelect id="trim" value={current.trim} onChange={(e) => selectTrim(e.target.value)} disabled={!current.modelYear || trimsQuery.isLoading}>
                      <option value="">Choose…</option>
                      {trimsQuery.data?.trims.map((t) => (
                        <option key={t.id} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                      <option value={OTHER}>Other — type it in</option>
                    </ModernSelect>
                  ) : (
                    <>
                      <ModernInput id="trim" placeholder="350" value={current.trim} onChange={(e) => set("trim", e.target.value)} disabled={!current.modelYear} />
                      {trimHasOptions && (
                        <button type="button" className="mt-1 text-[13px] text-[#0f6d86] underline" onClick={() => setTrimIsManual(false)}>
                          Choose from the list instead
                        </button>
                      )}
                    </>
                  )}
                  {!current.modelYear ? (
                    <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Choose a year first</p>
                  ) : (
                    !trimHasOptions && <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">No trims on file — type it in</p>
                  )}
                </ModernField>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Make" htmlFor="make">
                  <ModernInput id="make" placeholder="Lexus" value={current.make} onChange={(e) => set("make", e.target.value)} />
                </ModernField>
                <ModernField label="Model" htmlFor="model">
                  <ModernInput id="model" placeholder="ES" value={current.model} onChange={(e) => set("model", e.target.value)} />
                </ModernField>
              </div>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Year" htmlFor="year">
                  <ModernInput id="year" inputMode="numeric" placeholder="2008" value={current.modelYear} onChange={(e) => set("modelYear", e.target.value)} />
                </ModernField>
                <ModernField label="Trim" htmlFor="trim">
                  <ModernInput id="trim" placeholder="350" value={current.trim} onChange={(e) => set("trim", e.target.value)} />
                </ModernField>
              </div>
            </>
          )}

          <ModernField label="Engine" htmlFor="engine">
            <ModernInput id="engine" value={current.engineDescription} onChange={(e) => set("engineDescription", e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">e.g. &quot;3.5L, V6&quot;</p>
          </ModernField>

          {PLATE_DECODER_ENABLED ? (
            <>
              <ModernField label="License Plate" htmlFor="plate">
                <div className="flex gap-2">
                  <ModernInput
                    id="plate"
                    className="flex-1"
                    value={current.licensePlate}
                    onChange={(e) => set("licensePlate", e.target.value.toUpperCase())}
                  />
                  <ModernSelect aria-label="License plate state" className="!w-28" value={plateState} onChange={(e) => setPlateState(e.target.value)}>
                    {US_STATE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </ModernSelect>
                  <ModernButton
                    type="button"
                    variant="secondary"
                    className="whitespace-nowrap"
                    onClick={() => void decodePlate(true)}
                    disabled={decodingPlate || !current.licensePlate.trim() || !plateState}
                  >
                    {decodingPlate ? "Looking up…" : "Decode Plate"}
                  </ModernButton>
                </div>
                <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">
                  Add a state and decode to fill in the details above too — an additional option alongside the VIN decoder.
                </p>
                {plateDecodeNotice && <p className="mt-1 text-[13px] text-[#4a5361]">{plateDecodeNotice}</p>}
              </ModernField>

              {plateDecodedFields &&
                (plateDecodedFields.vin ||
                  plateDecodedFields.modelYear ||
                  plateDecodedFields.make ||
                  plateDecodedFields.model ||
                  PLATE_FIELD_LABELS.some(([key]) => plateDecodedFields[key])) && (
                  <div className="mb-5 rounded-[10px] border border-dashed border-[#e2e7ee] px-3.5 py-3">
                    <h3 className="mb-2 text-[14px] font-bold text-[#071a33]">From the Plate</h3>
                    <dl className="grid grid-cols-2 gap-y-1 text-[13px]">
                      {plateDecodedFields.vin && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">VIN</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.vin}</dd>
                        </div>
                      )}
                      {plateDecodedFields.modelYear && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">Year</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.modelYear}</dd>
                        </div>
                      )}
                      {plateDecodedFields.make && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">Make</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.make}</dd>
                        </div>
                      )}
                      {plateDecodedFields.model && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">Model</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.model}</dd>
                        </div>
                      )}
                      {PLATE_FIELD_LABELS.filter(([key]) => plateDecodedFields[key]).map(([key, label]) => (
                        <div key={key} className="contents">
                          <dt className="text-[#6b7a8f]">{label}</dt>
                          <dd className="text-right text-[#101b2d]">{String(plateDecodedFields[key])}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
            </>
          ) : (
            <ModernField label="License Plate" htmlFor="plate">
              <ModernInput id="plate" value={current.licensePlate} onChange={(e) => set("licensePlate", e.target.value.toUpperCase())} />
            </ModernField>
          )}

          {vehicleImageEnabled ? (
            <div className="grid grid-cols-2 gap-3.5">
              <ModernField label="Current Odometer" htmlFor="odometer">
                <ModernInput id="odometer" inputMode="numeric" value={current.lastOdometer} onChange={(e) => set("lastOdometer", e.target.value)} />
              </ModernField>
              <ModernField label="Vehicle Image URL" htmlFor="image-url">
                <ModernInput id="image-url" placeholder="https://…" value={current.imageUrl} onChange={(e) => set("imageUrl", e.target.value)} />
                <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Shown next to this vehicle on the customer page.</p>
              </ModernField>
            </div>
          ) : (
            <ModernField label="Current Odometer" htmlFor="odometer">
              <ModernInput id="odometer" inputMode="numeric" value={current.lastOdometer} onChange={(e) => set("lastOdometer", e.target.value)} />
            </ModernField>
          )}

          {error && <ModernErrorBanner message={error} />}

          <div className="flex gap-2.5">
            <ModernButton type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save Vehicle"}
            </ModernButton>
            <ModernButton type="button" variant="secondary" onClick={() => navigate(`/customers/${customerNumber}`)}>
              Cancel
            </ModernButton>
          </div>
        </form>
      </ModernCard>

      {canDelete && (
        <div className="mt-6 border-t border-[#e2e7ee] pt-6">
          <ModernConfirmButton
            variant="danger"
            confirmText="Permanently delete this vehicle, along with any of its voided work orders and invoices?"
            disabled={deleteMutation.isPending}
            onConfirm={() => deleteMutation.mutate()}
          >
            Delete Vehicle
          </ModernConfirmButton>
        </div>
      )}

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Save these changes to the vehicle?" />
    </div>
  );
}
